import { apiDelete, apiGet, apiPatch, apiPost } from '../../../shared/api-client/index.js';
import {
  entityDetailKey,
  entityListKey,
  type EntityListKey,
} from '../../../shared/api/entityQueryKeys.js';

export type MeetingStatus = 'scheduled' | 'completed' | 'cancelled';
export type ParticipantStatus = 'invited' | 'accepted' | 'declined';

export interface MeetingMemberRef {
  id: string;
  userId: string;
  user: { id: string; name: string; email: string };
}

/** Участник встречи: сотрудник, контакт ИЛИ внешний гость (участников у встречи много). */
export interface MeetingParticipant {
  id: string;
  status: ParticipantStatus;
  memberId?: string | null;
  contactId?: string | null;
  guestEmail?: string | null;
  guestName?: string | null;
  member?: MeetingMemberRef | null;
  contact?: {
    id: string;
    firstName: string;
    lastName?: string | null;
    email?: string | null;
  } | null;
}

export interface MeetingItem {
  id: string;
  workspaceId: string;
  title: string;
  description?: string | null;
  startsAt: string;
  endsAt: string;
  timezone: string;
  location?: string | null;
  status: MeetingStatus;
  createdAt: string;
  updatedAt: string;
  createdByMember?: MeetingMemberRef | null;
  ownerMember?: MeetingMemberRef | null;
  deal?: { id: string; title: string } | null;
  company?: { id: string; name: string } | null;
  contact?: {
    id: string;
    firstName: string;
    lastName?: string | null;
    email?: string | null;
  } | null;
  task?: { id: string; title: string } | null;
  document?: { id: string; title: string } | null;
  documentId?: string | null;
  project?: { id: string; name: string } | null;
  projectId?: string | null;
  participants: MeetingParticipant[];
}

export interface ParticipantInput {
  memberId?: string;
  contactId?: string;
  guestEmail?: string;
  guestName?: string;
}

export interface ListMeetingsFilters {
  q?: string;
  status?: MeetingStatus;
  dealId?: string;
  companyId?: string;
  contactId?: string;
  taskId?: string;
  documentId?: string;
  projectId?: string;
  /** Inclusive lower bound for startsAt, ISO 8601 datetime. */
  from?: string;
  /** Inclusive upper bound for startsAt, ISO 8601 datetime. */
  to?: string;
  /** Max results (1-500). Without it the list returns every meeting. */
  limit?: number;
}

export interface CreateMeetingInput {
  title: string;
  description?: string;
  startsAt: string;
  endsAt: string;
  timezone?: string;
  location?: string;
  ownerMemberId?: string;
  dealId?: string;
  companyId?: string;
  contactId?: string;
  taskId?: string;
  documentId?: string;
  projectId?: string;
  participants?: ParticipantInput[];
}

export interface UpdateMeetingInput {
  title?: string;
  description?: string | null;
  startsAt?: string;
  endsAt?: string;
  timezone?: string;
  location?: string | null;
  status?: MeetingStatus;
  ownerMemberId?: string | null;
  dealId?: string | null;
  companyId?: string | null;
  contactId?: string | null;
  taskId?: string | null;
  documentId?: string | null;
  projectId?: string | null;
  participants?: ParticipantInput[];
  expectedUpdatedAt?: string;
}

export function getMeetingsQueryKey(
  workspaceId: string,
  filters: ListMeetingsFilters = {},
): EntityListKey {
  return entityListKey('meetings', workspaceId, { ...filters });
}

export function getMeetingDetailQueryKey(
  workspaceId: string,
  meetingId: string,
): readonly string[] {
  return entityDetailKey('meetings', workspaceId, meetingId);
}

export async function getMeetingById(meetingId: string): Promise<MeetingItem> {
  return apiGet<MeetingItem>(`/meetings/${meetingId}`);
}

export async function listMeetings(filters: ListMeetingsFilters = {}): Promise<MeetingItem[]> {
  const search = new URLSearchParams();
  if (filters.q?.trim()) search.set('q', filters.q.trim());
  if (filters.status) search.set('status', filters.status);
  if (filters.dealId) search.set('dealId', filters.dealId);
  if (filters.companyId) search.set('companyId', filters.companyId);
  if (filters.contactId) search.set('contactId', filters.contactId);
  if (filters.taskId) search.set('taskId', filters.taskId);
  if (filters.documentId) search.set('documentId', filters.documentId);
  if (filters.projectId) search.set('projectId', filters.projectId);
  if (filters.from) search.set('from', filters.from);
  if (filters.to) search.set('to', filters.to);
  if (filters.limit !== undefined) search.set('limit', String(filters.limit));
  const path = search.size > 0 ? `/meetings?${search.toString()}` : '/meetings';
  return apiGet<MeetingItem[]>(path);
}

export async function createMeeting(input: CreateMeetingInput): Promise<MeetingItem> {
  return apiPost<MeetingItem, CreateMeetingInput>('/meetings', input);
}

export async function updateMeeting(id: string, input: UpdateMeetingInput): Promise<MeetingItem> {
  return apiPatch<MeetingItem, UpdateMeetingInput>(`/meetings/${id}`, input);
}

export async function deleteMeeting(id: string): Promise<void> {
  await apiDelete<void>(`/meetings/${id}`);
}

/** Ссылка на .ics — файл встречи для календаря участника. */
export function meetingIcsUrl(id: string): string {
  return `/api/v1/meetings/${id}/ics`;
}
